import { motion } from "framer-motion";
import { Check } from "lucide-react";

const points = [
  { title: "Strategy before design", desc: "We plan positioning, journeys, and KPIs before we pick a color." },
  { title: "SEO built from the foundation", desc: "Site architecture, schema, and speed baked in — not bolted on." },
  { title: "Clear communication", desc: "One project lead, weekly syncs, and a single shared roadmap." },
  { title: "Modern visuals with business purpose", desc: "Beautiful, yes — but every element earns its place with a goal." },
  { title: "Measurable results", desc: "Dashboards you actually understand, tied to revenue and pipeline." },
];

export default function WhyChoose() {
  return (
    <section data-testid="why-section" className="section-pad relative">
      <div className="container-apex grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-center">
        <div className="md:col-span-5">
          <div className="eyebrow mb-4">
            <span className="w-6 h-px bg-apex-cyan" /> Why APEX
          </div>
          <h2 className="text-4xl md:text-5xl font-extrabold leading-[1.05]">
            Human strategy, <span className="text-apex-orange">engineered outcomes.</span>
          </h2>
          <p className="mt-5 text-white/70 text-lg leading-relaxed">
            You don't need another agency deck — you need a partner that treats your growth like a system. Here's how we work differently.
          </p>

          <ul className="mt-8 space-y-4">
            {points.map((p, i) => (
              <motion.li
                key={p.title}
                initial={{ opacity: 0, x: -12 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                className="flex items-start gap-3.5"
                data-testid={`why-point-${i}`}
              >
                <span className="mt-0.5 inline-flex items-center justify-center w-6 h-6 rounded-lg bg-apex-orange/15 border border-apex-orange/30">
                  <Check className="w-3.5 h-3.5 text-apex-orange" strokeWidth={2.5} />
                </span>
                <div>
                  <div className="font-heading font-semibold">{p.title}</div>
                  <div className="text-sm text-white/65 leading-relaxed">{p.desc}</div>
                </div>
              </motion.li>
            ))}
          </ul>
        </div>

        {/* Right: animated dashboard preview */}
        <div className="md:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute inset-0 -z-10 bg-apex-radial" />
            <div className="glass glass-inner-glow p-5 md:p-7 relative">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
                  <span className="w-2.5 h-2.5 rounded-full bg-yellow-400/70" />
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" />
                </div>
                <div className="text-[11px] tracking-[0.28em] uppercase text-white/50">apex.growth/reports</div>
              </div>

              <div className="grid grid-cols-3 gap-3 md:gap-4">
                <StatCell label="Revenue Attributed" value="$482K" tone="orange" />
                <StatCell label="Leads This Month" value="1,208" tone="blue" />
                <StatCell label="Domain Authority" value="58 → 71" tone="cyan" />
              </div>

              <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs uppercase tracking-widest text-white/50">Organic sessions</div>
                  <div className="text-xs text-emerald-400">+142% YoY</div>
                </div>
                <AreaChart />
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3">
                <KeywordRow keyword="web design agency" position={3} change={+6} />
                <KeywordRow keyword="landing page design" position={5} change={+9} />
                <KeywordRow keyword="conversion optimization" position={8} change={+4} />
                <KeywordRow keyword="seo agency" position={11} change={+7} />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function StatCell({ label, value, tone }) {
  const map = {
    orange: "text-apex-orange",
    blue: "text-apex-blue",
    cyan: "text-apex-cyan",
  };
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
      <div className="text-[11px] uppercase tracking-widest text-white/55">{label}</div>
      <div className={`mt-1.5 font-heading font-bold text-2xl ${map[tone]}`}>{value}</div>
    </div>
  );
}

function AreaChart() {
  return (
    <svg viewBox="0 0 300 90" className="w-full h-24">
      <defs>
        <linearGradient id="area-fill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#246BFE" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#246BFE" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="area-line" x1="0" x2="1">
          <stop offset="0%" stopColor="#23D3FF" />
          <stop offset="100%" stopColor="#7C3AED" />
        </linearGradient>
      </defs>
      <motion.path
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1 }}
        d="M0,70 C30,60 50,65 70,55 C100,42 120,50 150,38 C180,26 210,32 240,20 C260,12 280,10 300,6 L300,90 L0,90 Z"
        fill="url(#area-fill)"
      />
      <motion.path
        initial={{ pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
        d="M0,70 C30,60 50,65 70,55 C100,42 120,50 150,38 C180,26 210,32 240,20 C260,12 280,10 300,6"
        fill="none"
        stroke="url(#area-line)"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function KeywordRow({ keyword, position, change }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2.5">
      <div className="text-xs text-white/85 truncate">{keyword}</div>
      <div className="flex items-center gap-2 text-xs">
        <span className="text-white/60">#{position}</span>
        <span className="text-emerald-400">▲ {change}</span>
      </div>
    </div>
  );
}
